import { Component, OnDestroy, OnInit, ViewChildren } from '@angular/core';
import { Router } from '@angular/router';
import { ActiveNavStateService } from '@app/modules/nav/services/active-nav-state.service';
import { NavStateService } from '@app/modules/nav/services/nav-state.service';
import { SurfaceUpgradePopoverComponent } from '@app/modules/surface/components/surface-upgrade-popover/surface-upgrade-popover.component';
import { SurfacePopoverStateService } from '@app/modules/surface/services/surface-popover-state.service';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-solid-svg-icons';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { filter, skip, Subject, takeUntil, tap } from 'rxjs';

@Component({
    selector: 'fdx-app-vertical-menu-collapsed',
    templateUrl: './app-vertical-menu-collapsed.component.html',
    styleUrls: ['./app-vertical-menu-collapsed.component.scss', '../app-vertical-menu/app-vertical-menu-buttons.scss'],
    standalone: false
})
export class AppVerticalMenuCollapsedComponent implements OnInit, OnDestroy {
    private readonly unsubscribe$: Subject<void> = new Subject<void>();
    protected readonly faArrowUpRightFromSquare: IconDefinition = faArrowUpRightFromSquare;

    @ViewChildren(NgbDropdown) ngbDropdowns: NgbDropdown[];
    @ViewChildren('upgradePopover') upgradePopovers: SurfaceUpgradePopoverComponent[];

    get anyPopoverOpen(): boolean {
        return this.upgradePopovers?.some((p) => p.isPopoverOpen);
    }

    constructor(
        protected readonly activeNavState: ActiveNavStateService,
        protected readonly navState: NavStateService,
        private readonly router: Router,
        private readonly surfacePopoverStateService: SurfacePopoverStateService
    ) { }

    ngOnInit(): void {
        this.bindPopoverStateService();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    private bindPopoverStateService(): void {
        this.surfacePopoverStateService.isBackdropOpen$.pipe(
            skip(1), // Skip the initial value
            filter((backdropOpen) => !backdropOpen),
            tap(() => this.closeDropdowns()),
            takeUntil(this.unsubscribe$)
        ).subscribe();
    }

    getNavGroupClass(id: string): string {
        return this.isNavGroupSelected(id)
            ? 'selected-item'
            : 'unselected-item';
    }

    getDropdownClasses(id: string, isGroup: boolean): string {
        return this.isDropdownSelected(id, isGroup)
            ? 'selected-item active-selected-item'
            : 'unselected-item';
    }

    /**
     * The nav group is included in the dropdown. The nav group is only selected if no sub nav menu items are selected.
     * @param id
     * @param isGroup
     */
    isDropdownSelected(id: string, isGroup: boolean): boolean {
        if (isGroup) {
            return this.activeNavState.isNavGroupSelected(id) && this.activeNavState.isSubNavUnset();
        }

        return this.activeNavState.isSubNavSelected(id);
    }

    /**
     * Unlike the expanded view - display as selected if the nav group OR subnav items have been selected
     * @param id
     */
    isNavGroupSelected(id: string): boolean {
        return this.activeNavState.isNavGroupSelected(id) || this.activeNavState.isSubNavSelected(id);
    }

    navClick(link: string[]): void {
        this.closeDropdowns();
        void this.router.navigate(link);
    }

    externalNavClick(link: string[]): void {
        this.closeDropdowns();
        window.open(link[0], '_blank', 'noopener');
    }

    private closeDropdowns(): void {
        this.ngbDropdowns.forEach((d) => {
            if (d.isOpen()) {
                d.close();
            }
        });
    }
}
